/**
 * Utility functions for sharing forms
 * @author Generated for Shared Forms Feature
 */

/**
 * Generates a shareable URL for a form
 * @param {string} formId - The form ID
 * @returns {string} The complete shareable URL
 */
export const generateShareableFormUrl = (formId) => {
  if (!formId) {
    throw new Error("Form ID is required to generate shareable URL");
  }

  return `${window.location.origin}/forms/${formId}`;
};

/**
 * Copies text to clipboard
 * @param {string} text - Text to copy
 * @returns {Promise<boolean>} Success status
 */
export const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      // Use modern clipboard API
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const success = document.execCommand("copy");
      document.body.removeChild(textArea);
      return success;
    }
  } catch (err) {
    console.error("Failed to copy text: ", err);
    return false;
  }
};

/**
 * Validates if a form ID is in the correct format
 * @param {string} formId - Form ID to validate
 * @returns {boolean} Is valid format
 */
export const isValidFormId = (formId) => {
  if (!formId || typeof formId !== "string") {
    return false;
  }

  // Basic validation - can be made more specific based on your form ID format
  const formIdPattern = /^[a-zA-Z0-9_-]+$/;
  return formIdPattern.test(formId) && formId.length > 0;
};

/**
 * Creates a QR code URL for the form (using a public QR service)
 * @param {string} formUrl - The complete form URL
 * @returns {string} QR code image URL
 */
export const generateQRCodeUrl = (formUrl) => {
  if (!formUrl) {
    throw new Error("Form URL is required to generate QR code");
  }

  // Using QR Server API (free service)
  const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/`;
  const params = new URLSearchParams({
    size: "200x200",
    data: formUrl,
    format: "png",
  });

  return `${qrApiUrl}?${params.toString()}`;
};

/**
 * Shares form using Web Share API (if available) or falls back to clipboard
 * @param {string} formId - The form ID
 * @param {string} formTitle - Optional form title for sharing
 * @returns {Promise<boolean>} Success status
 */
export const shareForm = async (formId, formTitle = "Form") => {
  const shareUrl = generateShareableFormUrl(formId);
  const shareData = {
    title: `Share ${formTitle}`,
    text: `Please fill out this form: ${formTitle}`,
    url: shareUrl,
  };

  try {
    // Check if Web Share API is available
    if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
      await navigator.share(shareData);
      return true;
    } else {
      // Fallback to copying URL to clipboard
      const copySuccess = await copyToClipboard(shareUrl);
      return copySuccess;
    }
  } catch (err) {
    console.error("Error sharing form:", err);
    // Try clipboard as final fallback
    return await copyToClipboard(shareUrl);
  }
};

/**
 * Downloads QR code for the form
 * @param {string} formId - The form ID
 * @param {string} fileName - Optional file name for download
 */
export const downloadFormQRCode = async (formId, fileName = "form-qr-code.png") => {
  try {
    const formUrl = generateShareableFormUrl(formId);
    const qrCodeUrl = generateQRCodeUrl(formUrl);

    const response = await fetch(qrCodeUrl);
    const blob = await response.blob();

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the object URL
    URL.revokeObjectURL(link.href);

    return true;
  } catch (err) {
    console.error("Error downloading QR code:", err);
    return false;
  }
};
